/**
 * Validation utilities for MetricData Dev Tool
 * @module MetricDataDevTool/utils/validators
 */

import { isValid, parse, isBefore, isAfter, differenceInDays } from 'date-fns';
import { METRIC_NAMES } from '../constants';

/**
 * Validate date range
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export function validateDateRange(startDate, endDate) {
  if (!startDate || !endDate) {
    return { valid: false, error: 'Both start and end dates are required' };
  }

  const start = parse(startDate, 'yyyy-MM-dd', new Date());
  const end = parse(endDate, 'yyyy-MM-dd', new Date());

  if (!isValid(start)) {
    return { valid: false, error: 'Invalid start date format' };
  }

  if (!isValid(end)) {
    return { valid: false, error: 'Invalid end date format' };
  }

  if (isAfter(start, end)) {
    return { valid: false, error: 'Start date must be before or equal to end date' };
  }

  const daysDiff = differenceInDays(end, start);
  if (daysDiff > 365) {
    return { valid: false, error: 'Date range cannot exceed 365 days' };
  }

  return { valid: true };
}

/**
 * Validate metric selection
 * @param {string[]} selectedMetrics - Array of selected metric names
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export function validateMetricSelection(selectedMetrics) {
  if (!selectedMetrics || selectedMetrics.length === 0) {
    return { valid: false, error: 'At least one metric must be selected' };
  }

  const invalidMetrics = selectedMetrics.filter(m => !METRIC_NAMES.includes(m));
  if (invalidMetrics.length > 0) {
    return {
      valid: false,
      error: `Invalid metrics: ${invalidMetrics.join(', ')}`
    };
  }

  return { valid: true };
}

/**
 * Validate aggregation options
 * @param {Object} aggregationOptions - Aggregation configuration
 * @param {boolean} aggregationOptions.byDate - Aggregate by date
 * @param {boolean} aggregationOptions.byDayPart - Aggregate by day part
 * @param {boolean} aggregationOptions.byDiningOption - Aggregate by dining option
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export function validateAggregationOptions(aggregationOptions) {
  if (!aggregationOptions || typeof aggregationOptions !== 'object') {
    return { valid: false, error: 'Aggregation options must be an object' };
  }

  const { byDate, byDayPart, byDiningOption } = aggregationOptions;

  if (typeof byDate !== 'boolean' ||
      typeof byDayPart !== 'boolean' ||
      typeof byDiningOption !== 'boolean') {
    return {
      valid: false,
      error: 'Aggregation options must be boolean values'
    };
  }

  return { valid: true };
}

/**
 * Validate store ID
 * @param {string} storeId - Store ID
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export function validateStoreId(storeId) {
  if (!storeId || typeof storeId !== 'string') {
    return { valid: false, error: 'Store ID is required' };
  }

  if (storeId.trim().length === 0) {
    return { valid: false, error: 'Store ID cannot be empty' };
  }

  return { valid: true };
}

/**
 * Validate all query inputs
 * @param {Object} params - Query parameters
 * @param {string} params.storeId - Store ID
 * @param {string} params.startDate - Start date
 * @param {string} params.endDate - End date
 * @param {string[]} params.selectedMetrics - Selected metrics
 * @param {Object} params.aggregationOptions - Aggregation options
 * @returns {{valid: boolean, errors: string[]}} Validation result with all errors
 */
export function validateQueryInputs(params) {
  const { storeId, startDate, endDate, selectedMetrics, aggregationOptions } = params;
  const errors = [];

  const storeValidation = validateStoreId(storeId);
  if (!storeValidation.valid) {
    errors.push(storeValidation.error);
  }

  const dateValidation = validateDateRange(startDate, endDate);
  if (!dateValidation.valid) {
    errors.push(dateValidation.error);
  }

  const metricValidation = validateMetricSelection(selectedMetrics);
  if (!metricValidation.valid) {
    errors.push(metricValidation.error);
  }

  if (aggregationOptions) {
    const aggValidation = validateAggregationOptions(aggregationOptions);
    if (!aggValidation.valid) {
      errors.push(aggValidation.error);
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}
